/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import {
	useGetLinkedAccountsQuery,
	useLazyGetHubspotLinkQuery,
	useLazyGetSquareLinkQuery
} from 'store/api/version-api.slice';

import HubSpotIcon from './hubspot_icon.png';
import SquareIcon from './square_icon.png';
import { styles } from './styles';

const path = {
	hubspot: {
		linked: 'hubspot_linked',
		url: 'link_hubspot'
	},
	square: {
		linked: 'square_linked',
		url: 'link_square'
	}
};

const LinkAccountsButton = ({ account, handleNext }) => {
	const [pollingInterval, setPollingInterval] = useState(1000);
	const { data: linkedStatus = {} } = useGetLinkedAccountsQuery({}, { pollingInterval });

	useEffect(() => {
		if (linkedStatus[path[account].linked]) {
			setPollingInterval(0);
		}
	}, [account, linkedStatus]);

	const [isLoading, setIsLoading] = useState(false);
	const [link, setLink] = useState(null);

	const [getHubspotLink] = useLazyGetHubspotLinkQuery();
	const [getSquareLink] = useLazyGetSquareLinkQuery();

	// * Configuring button style
	const linkSuccess = useMemo(
		() => ({
			...styles.button,
			...(linkedStatus[path[account].linked] && styles.buttonSuccess)
		}),
		[account, linkedStatus]
	);

	const handleLinkAccount = () => {
		setIsLoading(true);

		window.open(link, '_blank');
	};

	useEffect(() => {
		if (linkedStatus[path.hubspot.linked] && linkedStatus[path.square.linked]) {
			handleNext?.();
		}
	}, [linkedStatus, handleNext]);

	useEffect(() => {
		let mounted = true;

		const fn = account === 'hubspot' ? getHubspotLink : getSquareLink;

		fn().then(res => {
			if (mounted) {
				const _link = res.data[path[account].url];
				setLink(_link);
			}
		});

		return () => {
			mounted = false;
		};
	}, [account, getHubspotLink, getSquareLink]);

	const image_src = account === 'hubspot' ? HubSpotIcon : SquareIcon;

	return (
		<Box>
			<ListItemAvatar sx={styles.root}>
				<Fab aria-label="save" color="inherit" sx={linkSuccess} onClick={handleLinkAccount}>
					{linkedStatus[path[account].linked] ? (
						<CheckIcon />
					) : (
						<Box component="img" src={image_src} sx={{ height: 36 }} alt="Hubspot Logo" />
					)}
					{isLoading && <CircularProgress size={56} sx={styles.fabProgress} />}
				</Fab>
			</ListItemAvatar>
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				{account === 'square' && (
					<>
						<Typography component={'span'} variant="h5" color="primary">
							SQUARE
						</Typography>
						<Typography component={'span'} variant="subtitle2" color="secondary">
							LINK SQUARE
						</Typography>
					</>
				)}
			</Box>
		</Box>
	);
};

export default LinkAccountsButton;
